// ClientDetailsDialog.jsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Divider,
  Chip,
  Box,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";

const ClientDetailsDialog = ({ open, onClose, client }) => {
  if (!client) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "rounded-2xl" }}
    >
      <DialogTitle className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" className="font-bold text-gray-800">
            Client Details
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            className="hover:bg-blue-100"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers className="divide-y divide-gray-100">
        {/* Client Header */}
        <Box className="py-4">
          <Box display="flex" alignItems="center" className="mb-3">
            <Avatar className="w-16 h-16 bg-indigo-500 text-xl mr-4">
              {client.clientName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h5" className="font-bold text-gray-800">
                {client.clientName}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                ID: {client.clientId}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1} className="mt-2">
            <Chip
              icon={<BusinessIcon />}
              label={client.productName}
              size="small"
              className="bg-blue-100 text-blue-800"
            />
            <Chip
              icon={<CategoryIcon />}
              label={client.productCategory}
              size="small"
              className="bg-green-100 text-green-800"
            />
            {client.shortCode && (
              <Chip
                label={`Shortcode: ${client.shortCode}`}
                size="small"
                className="bg-purple-100 text-purple-800"
              />
            )}
          </Box>
        </Box>

        {/* Contact Information */}
        <Box className="py-4">
          <Typography
            variant="subtitle1"
            className="font-bold text-gray-800 mb-3"
          >
            Contact Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" className="mb-2">
                <EmailIcon className="text-gray-500 mr-2" />
                <Typography variant="body2" className="text-gray-700">
                  {client.officialEmail}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" className="mb-2">
                <PhoneIcon className="text-gray-500 mr-2" />
                <Typography variant="body2" className="text-gray-700">
                  {client.pocPhone || "Not provided"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" className="text-gray-600 mb-1">
                Point of Contact
              </Typography>
              <Typography variant="body2" className="text-gray-800 font-medium">
                {client.pocName} ({client.pocDesignation})
              </Typography>
            </Grid>

            {client.gstNumber && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className="text-gray-600 mb-1">
                  GST Number
                </Typography>
                <Typography
                  variant="body2"
                  className="text-gray-800 font-medium"
                >
                  {client.gstNumber}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Address Information */}
        <Box className="py-4">
          <Typography
            variant="subtitle1"
            className="font-bold text-gray-800 mb-3"
          >
            Address
          </Typography>

          <Box display="flex" alignItems="flex-start" className="mb-2">
            <LocationIcon className="text-gray-500 mr-2 mt-0.5" />
            <Box>
              <Typography variant="body2" className="text-gray-800">
                {client.addressLine}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                {client.city}, {client.state} {client.pincode}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                {client.country}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Additional Information */}
        <Box className="py-4">
          <Typography
            variant="subtitle1"
            className="font-bold text-gray-800 mb-3"
          >
            Additional Information
          </Typography>

          <Grid container spacing={2}>
            {client.specificRequirement && (
              <Grid item xs={12}>
                <Typography variant="body2" className="text-gray-600 mb-1">
                  Specific Requirements
                </Typography>
                <Typography variant="body2" className="text-gray-800">
                  {client.specificRequirement}
                </Typography>
              </Grid>
            )}

            {client.price && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" className="text-gray-600 mb-1">
                  Price
                </Typography>
                <Typography
                  variant="body2"
                  className="text-gray-800 font-medium"
                >
                  ${client.price}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" className="text-gray-600 mb-1">
                Created Date
              </Typography>
              <Typography variant="body2" className="text-gray-800">
                {new Date(client.createdAt).toLocaleDateString()}
              </Typography>
            </Grid>

            {client.clientQrId && (
              <Grid item xs={12}>
                <Typography variant="body2" className="text-gray-600 mb-1">
                  QR Code ID
                </Typography>
                <Typography variant="body2" className="text-gray-800 font-mono">
                  {client.clientQrId}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions className="p-4 border-t border-gray-100">
        <Button
          onClick={onClose}
          className="text-gray-700 hover:bg-gray-100 px-4"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientDetailsDialog;
